/**
 * SpectrumBar - Spectrum-style slider display for 5 STRUCT CODE axes
 *
 * Shows each axis as a horizontal spectrum between two poles:
 *   行動特性: 応答 ←→ 自発
 *   判断基準: 直感 ←→ 論理
 *   価値観:   実利 ←→ 理想
 *   共感性:   自律 ←→ 共感
 *   自己認識: 実践 ←→ 内省
 */

import React from 'react';
import styled, { useTheme } from 'styled-components';

/* ------------------------------------------------------------------ */
/*  Axis spectrum definitions                                          */
/* ------------------------------------------------------------------ */

const SPECTRUM_AXES = [
  { key: 'activation', label: '行動特性', labelEn: 'Behavioral', left: '応答', leftEn: 'Responsive', right: '自発', rightEn: 'Proactive' },
  { key: 'judgment',   label: '判断基準', labelEn: 'Decision',   left: '直感', leftEn: 'Intuitive', right: '論理', rightEn: 'Logical' },
  { key: 'choice',     label: '価値観',   labelEn: 'Values',     left: '実利', leftEn: 'Pragmatic', right: '理想', rightEn: 'Idealistic' },
  { key: 'resonance',  label: '共感性',   labelEn: 'Empathy',    left: '自律', leftEn: 'Autonomous', right: '共感', rightEn: 'Empathic' },
  { key: 'awareness',  label: '自己認識', labelEn: 'Self-Awareness', left: '実践', leftEn: 'Practical', right: '内省', rightEn: 'Reflective' },
];

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? '10px' : '16px')};
  width: 100%;
`;

const AxisRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AxisHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AxisLabel = styled.span`
  font-size: ${(props) => (props.$compact ? '0.75rem' : '0.85rem')};
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

const AxisValue = styled.span`
  font-size: ${(props) => (props.$compact ? '0.65rem' : '0.75rem')};
  font-family: ${(props) => props.theme.fonts.mono};
  color: ${(props) => props.theme.colors.textMuted};
`;

const TrackRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$compact ? '6px' : '8px')};
`;

const PoleLabel = styled.span`
  flex-shrink: 0;
  width: ${(props) => (props.$compact ? '24px' : '32px')};
  font-size: ${(props) => (props.$compact ? '0.65rem' : '0.75rem')};
  font-weight: 500;
  color: ${(props) => props.$active
    ? props.theme.colors.primary
    : props.theme.colors.textMuted};
  text-align: ${(props) => (props.$align === 'right' ? 'right' : 'left')};
`;

const Track = styled.div`
  flex: 1;
  height: ${(props) => (props.$compact ? '8px' : '12px')};
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 6px;
  position: relative;
  overflow: visible;
`;

const TrackFill = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${(props) => props.$color};
  border-radius: 6px;
  transition: all 0.4s ease;
  opacity: 0.3;
`;

const Indicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${(props) => (props.$compact ? '12px' : '16px')};
  height: ${(props) => (props.$compact ? '12px' : '16px')};
  border-radius: 50%;
  background: ${(props) => props.$color};
  border: 2px solid ${(props) => props.theme.colors.bgPrimary};
  box-shadow: 0 0 6px ${(props) => props.$color}66;
  transition: left 0.4s ease;
`;

const CenterLine = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 100%;
  background: ${(props) => props.theme.colors.border};
  opacity: 0.5;
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function SpectrumBar({
  axes = {},
  normalize = false,
  compact = false,
  lang = 'ja',
  showValue = true,
}) {
  const theme = useTheme();

  return (
    <Container $compact={compact}>
      {SPECTRUM_AXES.map(({ key, label, labelEn, left, leftEn, right, rightEn }) => {
        const defaultVal = normalize ? 0.5 : 500;
        const raw = axes[key] ?? defaultVal;
        const norm = normalize ? Math.min(Math.max(raw, 0), 1) : Math.min(Math.max(raw / 1000, 0), 1);
        const pct = norm * 100;

        // Determine which pole is active (left = high, right = low)
        const isLeft = norm >= 0.5;
        const color = theme.colors.primary;

        // Fill from center to indicator position
        const fillLeft = isLeft ? '50%' : `${pct}%`;
        const fillWidth = isLeft ? `${pct - 50}%` : `${50 - pct}%`;

        const displayLabel = lang === 'en' ? labelEn : label;
        const displayLeft = lang === 'en' ? leftEn : left;
        const displayRight = lang === 'en' ? rightEn : right;

        return (
          <AxisRow key={key}>
            <AxisHeader>
              <AxisLabel $compact={compact}>{displayLabel}</AxisLabel>
              {showValue && (
                <AxisValue $compact={compact}>
                  {Math.round(pct)}%
                </AxisValue>
              )}
            </AxisHeader>
            <TrackRow $compact={compact}>
              <PoleLabel $compact={compact} $align="right" $active={isLeft}>
                {displayLeft}
              </PoleLabel>
              <Track $compact={compact}>
                <CenterLine />
                <TrackFill
                  $color={color}
                  style={{ left: fillLeft, width: fillWidth }}
                />
                <Indicator
                  $compact={compact}
                  $color={color}
                  style={{ left: `${pct}%` }}
                />
              </Track>
              <PoleLabel $compact={compact} $align="left" $active={!isLeft}>
                {displayRight}
              </PoleLabel>
            </TrackRow>
          </AxisRow>
        );
      })}
    </Container>
  );
}

export default SpectrumBar;
